/**
 * MonthlyInsightExtras — renders the creative monthly-only sections that sit
 * below DaysInsightsReport in monthly report views.
 *
 * Fields consumed (all optional — component renders nothing if absent):
 *   monthly_highlights  string[]   Bright spot bullets from the month
 *   session_arc         string     How the month's energy evolved
 *   next_month_focus    string     One warm suggestion for next month
 *   child_spirit        string     Short evocative phrase capturing the child
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Star, TrendingUp, ArrowRight, Sparkles } from 'lucide-react';

export default function MonthlyInsightExtras({ data }) {
  if (!data) return null;

  const { monthly_highlights, session_arc, next_month_focus, child_spirit } = data;

  const hasHighlights = Array.isArray(monthly_highlights) && monthly_highlights.length > 0;
  const hasArc = Boolean(session_arc);
  const hasFocus = Boolean(next_month_focus);
  const hasSpirit = Boolean(child_spirit);

  if (!hasHighlights && !hasArc && !hasFocus && !hasSpirit) return null;

  return (
    <div className="mt-6 space-y-5">
      {/* Section divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-200 to-transparent" />
        <span className="text-xs font-semibold text-purple-500 uppercase tracking-widest px-2">
          Monthly Extras
        </span>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-200 to-transparent" />
      </div>

      {/* Child's spirit quote */}
      {hasSpirit && (
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
          <CardContent className="p-5 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">
                This Month's Spirit
              </p>
              <p className="text-lg font-semibold text-purple-900 italic">"{child_spirit}"</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Month's journey arc */}
      {hasArc && (
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-blue-800 text-sm">
              <TrendingUp className="w-4 h-4" /> Month's Journey
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-blue-900 leading-relaxed">{session_arc}</p>
          </CardContent>
        </Card>
      )}

      {/* Bright moments grid */}
      {hasHighlights && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
              Bright Moments This Month
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {monthly_highlights.map((highlight, i) => (
              <div
                key={i}
                className="flex items-start gap-2.5 p-3.5 rounded-xl bg-amber-50 border border-amber-200"
              >
                <span className="w-6 h-6 rounded-full bg-amber-400 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-amber-900 leading-relaxed">{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Looking ahead */}
      {hasFocus && (
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-emerald-800 text-sm">
              <ArrowRight className="w-4 h-4" /> Looking Ahead — Next Month
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm text-emerald-900 leading-relaxed">{next_month_focus}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
